import axios from "../../API/axios";
import React, { useEffect, useState } from "react";
import ReactSelect from "react-select";
import { useTranslation } from "react-i18next";
import { ShimmerDiv } from "@skeleton";
import SuccessToast from "../SuccessToast";
import WarningToast from "../WarningToast";
import ErrorToast from "../ErrorToast";
import useNewsFeedStore from "Views/NewsFeed/useNewsFeedStore";

const PRIVACY_FIELDS = [
  { key: "whoCanFollowMe", labelKey: "Who can follow me" },
  { key: "whoCanMessageMe", labelKey: "Who can message me" },
  { key: "whoCanSeeMyFriends", labelKey: "Who can see my friends" },
  { key: "whoCanTagMe", labelKey: "Who can tag me" },
  { key: "whoCanSeeBirthday", labelKey: "Who can see my birthday" },
  { key: "shareLocationWithPublic", labelKey: "Share my location with public" },
];

const EMPTY_CONFIG = {
  whoCanFollowMe: "",
  whoCanMessageMe: "",
  whoCanSeeMyFriends: "",
  whoCanTagMe: "",
  whoCanSeeBirthday: "",
  shareLocationWithPublic: "",
};

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 42,
    borderRadius: 4,
    backgroundColor: "var(--wlj-field-bg, #f4f6fb)",
    borderColor: "var(--wlj-field-border, #e4e7ec)",
    boxShadow: state.isFocused ? "var(--wlj-focus-ring, 0 0 0 3px rgba(0, 65, 159, 0.12))" : "none",
    "&:hover": { borderColor: "var(--buttonBase, #00419f)" },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 5,
    borderRadius: 4,
    background: "var(--wlj-card-bg, #fff)",
    border: "1px solid var(--wlj-border, #eef2f6)",
    overflow: "hidden",
    boxShadow: "0 12px 32px rgba(16, 24, 40, 0.14)",
  }),
  menuList: (provided) => ({
    ...provided,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    padding: 6,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: 13,
    borderRadius: 4,
    margin: "2px 4px",
    backgroundColor: "transparent",
    background: state.isSelected
      ? "var(--wlj-active-bg)"
      : state.isFocused
        ? "var(--wlj-hover-bg)"
        : "transparent",
    color: state.isSelected || state.isFocused ? "var(--buttonBase, #00419f)" : "inherit",
    fontWeight: state.isSelected ? 600 : 500,
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: 13,
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: 13,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
};

    
const mapResponseToState = (data) => {
  const source = data?.responseData ?? data ?? {};
  return {
    config: {
      whoCanFollowMe: source.whoCanFollowMe ?? "",
      whoCanMessageMe: source.whoCanMessageMe ?? "",
      whoCanSeeMyFriends: source.whoCanSeeMyFriends ?? "",
      whoCanTagMe: source.whoCanTagMe ?? "",
      whoCanSeeBirthday: source.whoCanSeeBirthday ?? "",
      shareLocationWithPublic: source.shareLocationWithPublic ?? "",
    },
    fieldOptions: source.options ?? {},
  };
};

const Privacy = () => {
  const { t } = useTranslation();
  const language = localStorage.getItem("Lang");

  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [fieldOptions, setFieldOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [updatingField, setUpdatingField] = useState(null);

  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [warningToastMessage, setWarningToastMessage] = useState("");
  const [showWarningToast, setShowWarningToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);

    // -------------------------------------------------------------------------
    const { setIsNewsPageLoaded } = useNewsFeedStore();

    useEffect(() => {
      setIsNewsPageLoaded(true); // enable right panel when entering NewsFeed
  
      return () => setIsNewsPageLoaded(false); // optional cleanup when leaving
    }, [setIsNewsPageLoaded]);
  
    // -------------------------------------------------------------------------
  
  const getLangKey = () => (language === "en" ? "" : language || "");

  const applyResponse = (data) => {
    const { config: newConfig, fieldOptions: newOptions } = mapResponseToState(data);
    setConfig(newConfig);
    if (newOptions && Object.keys(newOptions).length > 0) {
      setFieldOptions(newOptions);
    }
  };

  useEffect(() => {
    const fetchPrivacyConfig = async () => {
      const sessionId = localStorage.getItem("sessionId");
      const token = localStorage.getItem("token");

      if (!sessionId || !token) {
        setErrorToastMessage(t("User Profile.Authentication error"));
        setShowErrorToast(true);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const response = await axios.post(`/Account/PrivacyConfig`, {
          deviceType: "Web",
          sessionId,
          langKey: getLangKey(),
        });
        applyResponse(response.data);
      } catch (error) {
        if (error.response) {
          if (error.response.status === 404) {
            setWarningToastMessage(
              error.response.data.message ||
                t("User Profile.No privacy settings found")
            );
            setShowWarningToast(true);
          } else {
            setErrorToastMessage(
              error.response.data.message ||
                t("User Profile.Something went wrong")
            );
            setShowErrorToast(true);
          }
        } else {
          setErrorToastMessage(t("User Profile.Network error"));
          setShowErrorToast(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyConfig();
  }, [language, t]);

  const updatePrivacyConfig = async (field, updatedConfig, previousValue) => {
    const sessionId = localStorage.getItem("sessionId");
    const token = localStorage.getItem("token");

    if (!sessionId || !token) {
      setErrorToastMessage(t("User Profile.Authentication error"));
      setShowErrorToast(true);
      return;
    }

    setConfig(updatedConfig);
    setUpdatingField(field);

    const updateData = {
      deviceType: "Web",
      sessionId,
      langKey: getLangKey(),
      whoCanFollowMe: updatedConfig.whoCanFollowMe,
      whoCanMessageMe: updatedConfig.whoCanMessageMe,
      whoCanSeeMyFriends: updatedConfig.whoCanSeeMyFriends,
      whoCanTagMe: updatedConfig.whoCanTagMe,
      whoCanSeeBirthday: updatedConfig.whoCanSeeBirthday,
      shareLocationWithPublic: updatedConfig.shareLocationWithPublic,
    };

    try {
      const response = await axios.post(
        `/Account/UpdatePrivacyConfig`,
        updateData
      );
      applyResponse(response.data);
      setToastMessage(
        response.data?.message ||
          t("User Profile.Privacy settings updated successfully")
      );
      setShowToast(true);
    } catch (error) {
      setConfig((prev) => ({ ...prev, [field]: previousValue }));
      setErrorToastMessage(
        error.response?.data?.message ||
          t("User Profile.Failed to update privacy settings")
      );
      setShowErrorToast(true);
    } finally {
      setUpdatingField(null);
    }
  };

  const handleSelectChange = (field, selected) => {
    if (!selected || selected.value === config[field]) return;
    const previousValue = config[field];
    const updatedConfig = { ...config, [field]: selected.value };
    updatePrivacyConfig(field, updatedConfig, previousValue);
  };

  const getOptionForField = (field) => {
    const options = fieldOptions[field] || [];
    const value = config[field];
    const match = options.find((opt) => opt.value === value);
    if (match) return match;
    if (!value) return null;
    return { value, label: value };
  };

  const renderShimmerRows = () =>
    Array.from({ length: PRIVACY_FIELDS.length }, (_, index) => (
      <div className="privacy-row" key={`privacy-shimmer-${index}`}>
        <div className="privacy-label">
          <ShimmerDiv height={16} width="60%" rounded={4} />
        </div>
        <div className="privacy-control">
          <ShimmerDiv height={38} width="100%" rounded={8} />
        </div>
      </div>
    ));

  return (
    <>
      <div className="setting-header">
        <h4>{t("User Profile.Privacy Setting")}</h4>
      </div>

      <div className="privacy privacy-settings p-3">
        {loading ? (
          renderShimmerRows()
        ) : (
          PRIVACY_FIELDS.map(({ key, labelKey }) => (
            <div className="privacy-row" key={key}>
              <div className="privacy-label">
                <p className="mb-0">{t(`User Profile.${labelKey}`)}</p>
              </div>
              <div className="privacy-control">
                <ReactSelect
                  className="custom-select"
                  classNamePrefix="privacy-select"
                  styles={selectStyles}
                  options={fieldOptions[key] || []}
                  value={getOptionForField(key)}
                  onChange={(selected) => handleSelectChange(key, selected)}
                  placeholder={t("User Profile.Select option")}
                  isDisabled={
                    updatingField === key || !(fieldOptions[key]?.length > 0)
                  }
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>
          ))
        )}
      </div>

      {showToast && (
        <SuccessToast
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
      {showWarningToast && (
        <WarningToast
          message={warningToastMessage}
          onClose={() => setShowWarningToast(false)}
        />
      )}
      {showErrorToast && (
        <ErrorToast
          message={errorToastMessage}
          onClose={() => setShowErrorToast(false)}
        />
      )}
    </>
  );
};

export default Privacy;
